/**
 * QR encoder — byte mode, EC level M, versions 1–10. Ported verbatim from the approved
 * prototype (UI Concepts/hungery-overlay-simple.html), where the matrices were verified
 * identical to a reference implementation across all eight mask patterns.
 */

export interface QrMatrix {
  size: number;
  modules: boolean[][];
  version: number;
  mask: number;
}

const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);
for (let i = 0, x = 1; i < 255; i++) {
  EXP[i] = x;
  LOG[x] = i;
  x <<= 1;
  if (x & 0x100) {
    x ^= 0x11d;
  }
}
for (let i = 255; i < 512; i++) {
  EXP[i] = EXP[i - 255];
}

const mul = (a: number, b: number): number => (a === 0 || b === 0 ? 0 : EXP[LOG[a] + LOG[b]]);

function rsGen(n: number): number[] {
  let g = [1];
  for (let i = 0; i < n; i++) {
    const ng: number[] = new Array(g.length + 1).fill(0);
    for (let j = 0; j < g.length; j++) {
      ng[j] ^= g[j];
      ng[j + 1] ^= mul(g[j], EXP[i]);
    }
    g = ng;
  }
  return g;
}

function rsEncode(data: number[], ecLen: number): number[] {
  const gen = rsGen(ecLen);
  const res: number[] = new Array(data.length + ecLen).fill(0);
  for (let i = 0; i < data.length; i++) {
    res[i] = data[i];
  }
  for (let i = 0; i < data.length; i++) {
    const c = res[i];
    if (c === 0) {
      continue;
    }
    for (let j = 0; j < gen.length; j++) {
      res[i + j] ^= mul(gen[j], c);
    }
  }
  return res.slice(data.length);
}

/** Per version: [total codewords, EC codewords per block, [block count, data codewords][]]. */
const CAP: Record<number, [number, number, Array<[number, number]>]> = {
  1: [26, 10, [[1, 16]]],
  2: [44, 16, [[1, 28]]],
  3: [70, 26, [[1, 44]]],
  4: [100, 18, [[2, 32]]],
  5: [134, 24, [[2, 43]]],
  6: [172, 16, [[4, 27]]],
  7: [196, 18, [[4, 31]]],
  8: [242, 22, [[2, 38], [2, 39]]],
  9: [292, 22, [[3, 36], [2, 37]]],
  10: [346, 26, [[4, 43], [1, 44]]]
};
const ALIGN: Record<number, number[]> = {
  1: [], 2: [6, 18], 3: [6, 22], 4: [6, 26], 5: [6, 30],
  6: [6, 34], 7: [6, 22, 38], 8: [6, 24, 42], 9: [6, 26, 46], 10: [6, 28, 50]
};
const dataCount = (v: number): number => CAP[v][2].reduce((sum, [n, d]) => sum + n * d, 0);
const getBit = (x: number, i: number): boolean => ((x >>> i) & 1) !== 0;

export function encodeQr(text: string, forcedMask?: number): QrMatrix {
  const bytes = Array.from(new TextEncoder().encode(text));
  let version = 0;
  for (let v = 1; v <= 10; v++) {
    const cb = v <= 9 ? 8 : 16;
    if (4 + cb + bytes.length * 8 <= dataCount(v) * 8) {
      version = v;
      break;
    }
  }
  if (!version) {
    throw new Error('Link too long for a version-10 code (about 213 characters).');
  }
  const [, ecLen, blockSpec] = CAP[version];
  const countBits = version <= 9 ? 8 : 16;
  const bits: number[] = [];
  const push = (val: number, len: number): void => {
    for (let i = len - 1; i >= 0; i--) {
      bits.push((val >>> i) & 1);
    }
  };
  push(0b0100, 4);
  push(bytes.length, countBits);
  for (const b of bytes) {
    push(b, 8);
  }
  const capBits = dataCount(version) * 8;
  for (let i = 0; i < 4 && bits.length < capBits; i++) {
    bits.push(0);
  }
  while (bits.length % 8) {
    bits.push(0);
  }
  const dataCw: number[] = [];
  for (let i = 0; i < bits.length; i += 8) {
    let b = 0;
    for (let j = 0; j < 8; j++) {
      b = (b << 1) | bits[i + j];
    }
    dataCw.push(b);
  }
  const pad = [0xec, 0x11];
  for (let i = 0; dataCw.length < dataCount(version); i++) {
    dataCw.push(pad[i % 2]);
  }
  const dB: number[][] = [];
  const eB: number[][] = [];
  let p = 0;
  for (const [num, len] of blockSpec) {
    for (let i = 0; i < num; i++) {
      const chunk = dataCw.slice(p, p + len);
      p += len;
      dB.push(chunk);
      eB.push(rsEncode(chunk, ecLen));
    }
  }
  const cw: number[] = [];
  const maxData = Math.max(...dB.map((b) => b.length));
  for (let i = 0; i < maxData; i++) {
    for (const b of dB) {
      if (i < b.length) {
        cw.push(b[i]);
      }
    }
  }
  for (let i = 0; i < ecLen; i++) {
    for (const b of eB) {
      cw.push(b[i]);
    }
  }

  const size = version * 4 + 17;
  const m: boolean[][] = Array.from({ length: size }, () => new Array(size).fill(false));
  const fn: boolean[][] = Array.from({ length: size }, () => new Array(size).fill(false));
  const set = (x: number, y: number, d: boolean): void => {
    m[y][x] = d;
    fn[y][x] = true;
  };
  const finder = (cx: number, cy: number): void => {
    for (let dy = -4; dy <= 4; dy++) {
      for (let dx = -4; dx <= 4; dx++) {
        const x = cx + dx;
        const y = cy + dy;
        if (x < 0 || y < 0 || x >= size || y >= size) {
          continue;
        }
        const d = Math.max(Math.abs(dx), Math.abs(dy));
        set(x, y, d !== 2 && d !== 4);
      }
    }
  };
  finder(3, 3);
  finder(size - 4, 3);
  finder(3, size - 4);
  for (let i = 8; i < size - 8; i++) {
    set(6, i, i % 2 === 0);
    set(i, 6, i % 2 === 0);
  }
  const al = ALIGN[version];
  for (const ay of al) {
    for (const ax of al) {
      if ((ax === 6 && ay === 6) || (ax === 6 && ay === size - 7) || (ax === size - 7 && ay === 6)) {
        continue;
      }
      for (let dy = -2; dy <= 2; dy++) {
        for (let dx = -2; dx <= 2; dx++) {
          set(ax + dx, ay + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
        }
      }
    }
  }
  for (let i = 0; i <= 8; i++) {
    if (i !== 6) {
      set(8, i, false);
      set(i, 8, false);
    }
  }
  for (let i = 0; i < 8; i++) {
    set(size - 1 - i, 8, false);
    set(8, size - 1 - i, false);
  }
  set(8, size - 8, true);
  if (version >= 7) {
    let rem = version;
    for (let i = 0; i < 12; i++) {
      rem = (rem << 1) ^ ((rem >>> 11) * 0x1f25);
    }
    const vb = (version << 12) | rem;
    for (let i = 0; i < 18; i++) {
      const bit = getBit(vb, i);
      const a = size - 11 + (i % 3);
      const b = Math.floor(i / 3);
      set(a, b, bit);
      set(b, a, bit);
    }
  }
  let bi = 0;
  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) {
      right = 5;
    }
    for (let vert = 0; vert < size; vert++) {
      for (let j = 0; j < 2; j++) {
        const x = right - j;
        const upward = ((right + 1) & 2) === 0;
        const y = upward ? size - 1 - vert : vert;
        if (!fn[y][x] && bi < cw.length * 8) {
          m[y][x] = getBit(cw[bi >>> 3], 7 - (bi & 7));
          bi++;
        }
      }
    }
  }
  const maskFn: Array<(x: number, y: number) => boolean> = [
    (x, y) => (x + y) % 2 === 0,
    (x, y) => y % 2 === 0,
    (x) => x % 3 === 0,
    (x, y) => (x + y) % 3 === 0,
    (x, y) => (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0,
    (x, y) => ((x * y) % 2) + ((x * y) % 3) === 0,
    (x, y) => (((x * y) % 2) + ((x * y) % 3)) % 2 === 0,
    (x, y) => (((x + y) % 2) + ((x * y) % 3)) % 2 === 0
  ];
  const applyMask = (k: number): void => {
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        if (!fn[y][x] && maskFn[k](x, y)) {
          m[y][x] = !m[y][x];
        }
      }
    }
  };
  const drawFormat = (k: number): void => {
    let rem = k;
    for (let i = 0; i < 10; i++) {
      rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
    }
    const fb = ((k << 10) | rem) ^ 0x5412;
    for (let i = 0; i <= 5; i++) {
      m[i][8] = getBit(fb, i);
    }
    m[7][8] = getBit(fb, 6);
    m[8][8] = getBit(fb, 7);
    m[8][7] = getBit(fb, 8);
    for (let i = 9; i < 15; i++) {
      m[8][14 - i] = getBit(fb, i);
    }
    for (let i = 0; i < 8; i++) {
      m[8][size - 1 - i] = getBit(fb, i);
    }
    for (let i = 8; i < 15; i++) {
      m[size - 15 + i][8] = getBit(fb, i);
    }
    m[size - 8][8] = true;
  };
  const penalty = (): number => {
    let score = 0;
    for (let i = 0; i < size; i++) {
      for (const row of [true, false]) {
        let run = 1;
        let prev = row ? m[i][0] : m[0][i];
        for (let j = 1; j < size; j++) {
          const c = row ? m[i][j] : m[j][i];
          if (c === prev) {
            run++;
          } else {
            if (run >= 5) {
              score += 3 + (run - 5);
            }
            run = 1;
            prev = c;
          }
        }
        if (run >= 5) {
          score += 3 + (run - 5);
        }
      }
    }
    for (let y = 0; y < size - 1; y++) {
      for (let x = 0; x < size - 1; x++) {
        const c = m[y][x];
        if (c === m[y][x + 1] && c === m[y + 1][x] && c === m[y + 1][x + 1]) {
          score += 3;
        }
      }
    }
    const pats = [
      [1, 0, 1, 1, 1, 0, 1, 0, 0, 0, 0],
      [0, 0, 0, 0, 1, 0, 1, 1, 1, 0, 1]
    ];
    for (let i = 0; i < size; i++) {
      for (let j = 0; j <= size - 11; j++) {
        for (const pt of pats) {
          let okR = true;
          let okC = true;
          for (let k = 0; k < 11; k++) {
            if (m[i][j + k] !== !!pt[k]) {
              okR = false;
            }
            if (m[j + k][i] !== !!pt[k]) {
              okC = false;
            }
          }
          if (okR) {
            score += 40;
          }
          if (okC) {
            score += 40;
          }
        }
      }
    }
    let dark = 0;
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        if (m[y][x]) {
          dark++;
        }
      }
    }
    score += 10 * Math.floor(Math.abs((dark * 100) / (size * size) - 50) / 5);
    return score;
  };
  if (forcedMask != null) {
    applyMask(forcedMask);
    drawFormat(forcedMask);
    return { size, modules: m, version, mask: forcedMask };
  }
  let best = 0;
  let bestScore = Infinity;
  for (let k = 0; k < 8; k++) {
    applyMask(k);
    drawFormat(k);
    const sc = penalty();
    if (sc < bestScore) {
      bestScore = sc;
      best = k;
    }
    applyMask(k);
  }
  applyMask(best);
  drawFormat(best);
  return { size, modules: m, version, mask: best };
}
